import React, { Component } from 'react';
import { AuthContext } from '../context/AuthContext';
import { NotificationsContext } from '../context/NotificationsContext';
import { useNavigate, useLocation } from 'react-router-dom';
import BrandName from './BrandName';
import NotificationIcon from './NotificationIcon';
import api from '../services/api';
import UserMenu from './UserMenu';

class Navbar extends Component {
  static contextType = AuthContext;

  constructor(props) {
    super(props);
    this.state = {
      showUserMenu: false,
      showNotificationDropdown: false,
    };
    
    this.userMenuRef = React.createRef();
    this.notificationDropdownRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleClickOutside = (event) => {
    // Close user menu if clicked outside
    if (this.userMenuRef.current && !this.userMenuRef.current.contains(event.target)) {
      this.setState({ showUserMenu: false });
    }
    
    // Close notification dropdown if clicked outside
    if (this.notificationDropdownRef.current && !this.notificationDropdownRef.current.contains(event.target)) {
      this.setState({ showNotificationDropdown: false });
    }
  };

  toggleNotificationDropdown = () => {
    this.setState(prevState => ({ 
      showNotificationDropdown: !prevState.showNotificationDropdown,
      showUserMenu: false
    }));
  };

  toggleUserMenu = () => {
    this.setState(prevState => ({ 
      showUserMenu: !prevState.showUserMenu,
      showNotificationDropdown: false
    }));
  };

  navigateTo = (path) => {
    this.props.navigate(path);
    this.setState({ showNotificationDropdown: false, showUserMenu: false });
  };

  getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  getNotificationAvatarColor = (type) => {
    const colors = {
      job_shared: 'bg-gradient-to-br from-blue-500 to-blue-600',
      job_created: 'bg-gradient-to-br from-green-500 to-green-600',
      job_updated: 'bg-gradient-to-br from-yellow-500 to-yellow-600',
      job_deleted: 'bg-gradient-to-br from-red-500 to-red-600',
      user_invited: 'bg-gradient-to-br from-purple-500 to-purple-600',
      invitation_accepted: 'bg-gradient-to-br from-pink-500 to-pink-600',
      user_registered: 'bg-gradient-to-br from-indigo-500 to-indigo-600',
      password_changed: 'bg-gradient-to-br from-orange-500 to-orange-600',
      email_changed: 'bg-gradient-to-br from-cyan-500 to-cyan-600',
    };
    
    return colors[type] || 'bg-gradient-to-br from-slate-500 to-slate-600';
  };

  formatNotificationTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  handleNotificationClick = (notification, markAsRead) => async () => {
    // Mark as read if unread
    if (!notification.read && markAsRead) {
      await markAsRead(notification.id);
    }

    // Navigate based on notification type
    if (notification.relatedJobId) {
      this.navigateTo('/jobs');
    }

    // Close dropdown
    this.setState({ showNotificationDropdown: false });
  };

  handleMarkAllAsRead = (markAllAsRead) => async () => {
    if (markAllAsRead) {
      await markAllAsRead();
    }
  };

  handleDeleteAllNotifications = (refreshNotifications) => async () => {
    if (window.confirm('Delete all notifications? This cannot be undone.')) {
      try {
        await api.delete('/notifications');
        if (refreshNotifications) {
          await refreshNotifications();
        }
        this.setState({ showNotificationDropdown: false });
      } catch (error) {
        console.error('Failed to delete all notifications:', error);
      }
    }
  };

  render() {
    const { user } = this.context;
    const { showUserMenu, showNotificationDropdown } = this.state;
    const { location } = this.props;
    const isAdmin = user?.admin;

    return (
      <NotificationsContext.Consumer>
        {({ notifications, unreadCount, markAsRead, markAllAsRead, refreshNotifications }) => (
          <header className="bg-white shadow-sm border-b border-slate-200 sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center h-16">
                {/* Logo and Navigation */}
                <div className="flex items-center space-x-8">
                  <div className="flex items-center space-x-3 cursor-pointer" onClick={() => this.navigateTo('/jobs')}>
                    <img 
                      src="/logo.png" 
                      alt="PlansSource Logo" 
                      className="w-12 h-12 object-contain"
                    />
                    <h1 className="text-xl">
                      <BrandName size="xl" />
                    </h1>
                  </div>
                  {user && (
                    <nav className="hidden md:flex space-x-1">
                      <button
                        onClick={() => this.navigateTo('/jobs')}
                        className={`px-4 py-2 rounded-lg font-medium transition ${
                          location.pathname === '/jobs'
                            ? 'bg-slate-100 text-slate-900'
                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                        }`}
                      >
                        My Jobs
                      </button>
                      <button
                        onClick={() => this.navigateTo('/reports')}
                        className={`px-4 py-2 rounded-lg font-medium transition ${
                          location.pathname === '/reports'
                            ? 'bg-slate-100 text-slate-900'
                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                        }`}
                      >
                        Reports
                      </button>
                    </nav>
                  )}
                </div>

                {/* Right Side: Notifications + User Menu */}
                {user && (
                  <div className="flex items-center space-x-4">
                    {/* Notifications Dropdown */}
                    <div className="relative" ref={this.notificationDropdownRef}>
                      <button
                        onClick={this.toggleNotificationDropdown}
                        className="relative p-2 rounded-lg hover:bg-slate-100 transition"
                      >
                        <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                        </svg>
                        {unreadCount > 0 && (
                          <span className="absolute top-1 right-1 w-5 h-5 bg-gradient-to-br from-red-500 to-pink-600 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-lg">
                            {unreadCount > 9 ? '9+' : unreadCount}
                          </span>
                        )}
                      </button>

                      {showNotificationDropdown && (
                        <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden">
                          {/* Header */}
                          <div className="px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-teal-500 to-cyan-600">
                            <div className="flex items-center justify-between">
                              <h3 className="text-sm font-semibold text-white">Notifications</h3>
                              {notifications.length > 0 && (
                                <div className="flex items-center space-x-2">
                                  {unreadCount > 0 && (
                                    <button
                                      onClick={this.handleMarkAllAsRead(markAllAsRead)}
                                      className="text-xs text-white/90 hover:text-white font-medium transition"
                                    >
                                      Mark all read
                                    </button>
                                  )}
                                  <button
                                    onClick={this.handleDeleteAllNotifications(refreshNotifications)}
                                    className="text-xs text-white/90 hover:text-white font-medium transition"
                                  >
                                    Clear all
                                  </button>
                                </div>
                              )}
                            </div>
                            {unreadCount > 0 && (
                              <p className="text-xs text-white/80 mt-1">{unreadCount} unread</p>
                            )}
                          </div>

                          {/* Notifications List */}
                          <div className="max-h-96 overflow-y-auto">
                            {notifications.length === 0 ? (
                              <div className="px-4 py-12 text-center">
                                <svg className="w-12 h-12 text-slate-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                </svg>
                                <p className="text-sm text-slate-500">No notifications</p>
                                <p className="text-xs text-slate-400 mt-1">You're all caught up!</p>
                              </div>
                            ) : (
                              notifications.slice(0, 5).map((notification) => (
                                <div
                                  key={notification.id}
                                  className={`px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition cursor-pointer ${
                                    !notification.read ? 'bg-blue-50' : ''
                                  }`}
                                  onClick={this.handleNotificationClick(notification, markAsRead)}
                                >
                                  <div className="flex items-start space-x-3">
                                    {/* Avatar Circle */}
                                    {notification.relatedUserName ? (
                                      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm ${this.getNotificationAvatarColor(notification.type)}`}>
                                        {this.getInitials(notification.relatedUserName)}
                                      </div>
                                    ) : (
                                      <div className="flex-shrink-0 mt-1">
                                        <NotificationIcon type={notification.type} size="medium" />
                                      </div>
                                    )}

                                    {/* Content */}
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-slate-900 line-clamp-1">
                                        {notification.title}
                                      </p>
                                      <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">
                                        {notification.message}
                                      </p>
                                      <p className="text-xs text-slate-400 mt-1">
                                        {this.formatNotificationTime(notification.createdAt)}
                                      </p>
                                    </div>

                                    {/* Unread Indicator */}
                                    {!notification.read && (
                                      <div className="flex-shrink-0">
                                        <div className="w-2 h-2 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full"></div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>

                          {/* Footer */}
                          {notifications.length > 0 && (
                            <div className="px-4 py-3 border-t border-slate-200 bg-slate-50">
                              <button
                                onClick={() => {
                                  this.setState({ showNotificationDropdown: false });
                                  this.navigateTo('/notifications');
                                }}
                                className="text-sm text-teal-600 hover:text-teal-700 font-medium w-full text-center"
                              >
                                View all notifications
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* User Menu Component */}
                    <UserMenu 
                      isAdmin={isAdmin}
                      onNavigate={this.navigateTo}
                      onToggle={this.toggleUserMenu}
                    />
                  </div>
                )}
              </div>
            </div>
          </header>
        )}
      </NotificationsContext.Consumer>
    );
  }
}

const NavbarWithRouter = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  return <Navbar {...props} navigate={navigate} location={location} />;
};

export default NavbarWithRouter;